import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useRouter } from 'next/router';
import { cartOrderPublicClient } from '../../services/cart-order/client';
import {
  QuestionLogicalId,
  QuizAnswer,
  QuizApiQuestion,
  QuizApiResponse,
  QuizSubmissionResponse,
} from './types';
import Welcome from './Welcome';
import Quantity from './questions/Quantity';
import Review from './Review';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  CreateCartMutation,
  CreateCartMutationVariables,
} from '../../ts/@types/generated/graphql/cart-order-service/graphql';
import { CREATE_CART } from '../../graphql-operations/cart-order-service/carts.mutation';
import { ClientNames } from '../../utils/apolloClient';
import {
  CART_ID_COOKIE_KEY,
  DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
} from '../../utils/constants';
import { getSubscriptionRecurringBooleanFromConfig } from '../../utils/getSubscriptionRecurringBooleanFromConfig';
import useNonNullContext from '../../hooks/useNonNullContext';
import ConfigContext from '../../context/config';
import {
  IngredientToAvoid,
  MealSize,
} from '../../ts/@types/generated/graphql/catalog-service/graphql';
import JsCookie from 'js-cookie';
import QuantityByMealType from './questions/QuantityByMealType';
import EatingStyle from './questions/EatingStyle';
import { axiosClient } from '../../utils/axiosClient';
import { TagCategory } from '../../services/panacea/types';
import UserContext from '../../context/user';
import { getIngredientsToAvoid } from '../../services/ecomm-catalog/ingredients-to-avoid';
import IngredientsToAvoid from './questions/IngredientsToAvoid';
import _ from 'lodash';
import PrimaryProtein from './questions/PrimaryProtein';
import { buildMenuFilterQueryParams } from './build-menu-filter-query-params';

export default function PersonalizedPlanQuiz() {
  const tenantConfig = useNonNullContext(ConfigContext);
  const { tenantId } = useNonNullContext(UserContext);
  const router = useRouter();
  const apolloClient = useApolloClient();

  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);
  const [questions, setQuestions] = useState<QuizApiQuestion[]>([]);
  const [tagCategories, setTagCategories] = useState<TagCategory[]>([]);
  const [ingredientsToAvoid, setIngredientsToAvoid] = useState<
    IngredientToAvoid[]
  >([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await cartOrderPublicClient(1).get<QuizApiResponse>(
          '/personalized-plan/quiz',
        );
        const { questions } = res.data;
        if (!questions?.length) {
          await router.push('/');
        }

        setQuestions(questions);
        setIsLoadingQuiz(false);
      } catch {
        // @TODO: Display an error
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      const response = await axiosClient.get<TagCategory[]>(
        '/api/v1/ecomm-catalog-rest/tags/tag-categories/tenant',
        {
          params: {
            tenantId,
          },
        },
      );
      setTagCategories(response.data);
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      const ingredientsToAvoid = await getIngredientsToAvoid({ apolloClient });
      const sortedIngredients = _.sortBy(
        ingredientsToAvoid,
        (ingredient) => ingredient.displayName,
      );
      const transformedIngredients = sortedIngredients.map((ingredient) => {
        return {
          ...ingredient,
          displayName: ingredient.displayName.replace('at all', '').trim(),
        };
      });
      setIngredientsToAvoid(transformedIngredients);
    })();
  }, []);

  const dietaryTemplateTags =
    tagCategories.find(
      (tagCategory) => tagCategory.logicalId === 'DIETARY_TEMPLATE',
    )?.tags || [];
  const primaryProteinTags =
    tagCategories.find(
      (tagCategory) => tagCategory.logicalId === 'PRIMARY_PROTEIN',
    )?.tags || [];

  // Step sequence: welcome + tenant questions + review
  const steps = useMemo(
    () => ['WELCOME', ...questions.map((q) => q.logicalId), 'REVIEW'] as const,
    [questions.length],
  );

  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Partial<QuizAnswer>>>(
    {},
  );

  const currentQuestionLogicalId = steps[stepIdx];

  const questionTotal = steps.length - 1; // excluding 'WELCOME'
  const questionIdx = currentQuestionLogicalId === 'WELCOME' ? -1 : stepIdx - 1;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepIdx]);

  const next = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));
  const skip = () => router.push('/menu');

  const jumpTo = (idOrIdx: QuestionLogicalId | number) => {
    if (typeof idOrIdx === 'number') {
      setStepIdx(idOrIdx);
      return;
    }
    const i = steps.indexOf(idOrIdx as (typeof steps)[number]);
    if (i >= 0) {
      setStepIdx(i);
    }
  };

  const [createCartMutation] = useMutation<
    CreateCartMutation,
    CreateCartMutationVariables
  >(CREATE_CART);
  const createCart: typeof createCartMutation = (options) =>
    createCartMutation({
      ...options,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
    });

  const handleComplete = async (
    zipCode: string,
    fullName?: string,
    email?: string,
  ) => {
    setIsSubmitting(true);
    const payload = questions.map((question) => ({
      questionLogicalId: question.logicalId,
      ...(answers[question.logicalId] ?? {}),
    }));
    const quizSubmissionResult = await cartOrderPublicClient(1).post<
      Partial<QuizSubmissionResponse> & { mealQuantity: number }
    >('/personalized-plan/quiz/answers', payload);

    const { data } = await createCart({
      variables: {
        cartData: {
          recipientName: fullName || '',
          tenantEligibilityConfigLogicalId:
            DEFAULT_TENANT_ELIGIBILITY_CONFIG_LOGICAL_ID,
          populationCriteria: quizSubmissionResult.data,
        },
        funnelData: {
          email: email || '',
          name: fullName || '',
          zip: zipCode,
          recurring: getSubscriptionRecurringBooleanFromConfig(
            tenantConfig.subscriptionToggleConfiguration,
          ),
          mealSize: MealSize.Standard,
        },
      },
    });
    if (!data?.createCart) {
      setIsSubmitting(false);
      throw new Error('Failed to create cart');
    }

    JsCookie.set(CART_ID_COOKIE_KEY, data.createCart.uuid);
    await router.push({
      pathname: '/menu',
      query: {
        cartId: data.createCart.uuid,
        ...buildMenuFilterQueryParams(quizSubmissionResult.data),
      },
    });
  };

  let step: React.ReactNode = null;

  if (currentQuestionLogicalId === 'WELCOME') {
    step = <Welcome onNext={next} onSkip={skip} />;
  } else if (currentQuestionLogicalId === 'REVIEW') {
    step = (
      <Review
        answers={answers}
        setAnswers={setAnswers}
        onNext={handleComplete}
        onBack={back}
        stepIdx={questionIdx}
        total={questionTotal}
        jumpTo={jumpTo}
        questionLogicalIds={questions.map(
          ({ logicalId }) => logicalId as QuestionLogicalId,
        )}
        isSubmitting={isSubmitting}
        tags={[...dietaryTemplateTags, ...primaryProteinTags]}
        ingredientsToAvoid={ingredientsToAvoid}
      />
    );
  } else if (currentQuestionLogicalId === 'QUANTITY') {
    step = (
      <Quantity
        state={answers['QUANTITY']}
        setState={(answer: Partial<QuizAnswer>) => {
          setAnswers((prevState) => {
            return {
              ...prevState,
              QUANTITY: answer,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
      />
    );
  } else if (currentQuestionLogicalId === 'QUANTITY_BY_MEAL_TYPE') {
    step = (
      <QuantityByMealType
        state={answers['QUANTITY_BY_MEAL_TYPE']}
        setState={(answer: Partial<QuizAnswer>) => {
          setAnswers((prevState) => {
            return {
              ...prevState,
              QUANTITY_BY_MEAL_TYPE: answer,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
      />
    );
  } else if (currentQuestionLogicalId === 'EATING_STYLE') {
    step = (
      <EatingStyle
        state={answers['EATING_STYLE']}
        setState={(action: React.SetStateAction<Partial<QuizAnswer>>) => {
          setAnswers((prevState) => {
            const prev = prevState['EATING_STYLE'] ?? {};
            return {
              ...prevState,
              EATING_STYLE:
                typeof action === 'function' ? action(prev) : action,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
        tags={dietaryTemplateTags}
      />
    );
  } else if (currentQuestionLogicalId === 'INGREDIENTS_TO_AVOID') {
    step = (
      <IngredientsToAvoid
        state={answers['INGREDIENTS_TO_AVOID']}
        setState={(action: React.SetStateAction<Partial<QuizAnswer>>) => {
          setAnswers((prevState) => {
            const prev = prevState['INGREDIENTS_TO_AVOID'] ?? {};
            return {
              ...prevState,
              INGREDIENTS_TO_AVOID:
                typeof action === 'function' ? action(prev) : action,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
        ingredients={ingredientsToAvoid}
      />
    );
  } else if (currentQuestionLogicalId === 'COMMON_ALLERGENS') {
    step = (
      <IngredientsToAvoid
        state={answers['COMMON_ALLERGENS']}
        setState={(action: React.SetStateAction<Partial<QuizAnswer>>) => {
          setAnswers((prevState) => {
            const prev = prevState['COMMON_ALLERGENS'] ?? {};
            return {
              ...prevState,
              COMMON_ALLERGENS:
                typeof action === 'function' ? action(prev) : action,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
        ingredients={ingredientsToAvoid}
        hideNonAllergens={true}
      />
    );
  } else if (currentQuestionLogicalId === 'PRIMARY_PROTEIN') {
    step = (
      <PrimaryProtein
        state={answers['PRIMARY_PROTEIN']}
        setState={(action: React.SetStateAction<Partial<QuizAnswer>>) => {
          setAnswers((prevState) => {
            const prev = prevState['PRIMARY_PROTEIN'] ?? {};
            return {
              ...prevState,
              PRIMARY_PROTEIN:
                typeof action === 'function' ? action(prev) : action,
            };
          });
        }}
        onNext={next}
        onBack={stepIdx > 0 ? back : null}
        stepIdx={questionIdx}
        total={questionTotal}
        tags={primaryProteinTags}
      />
    );
  }

  if (isLoadingQuiz) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ width: '100%', height: '100%' }}>{step}</Box>
    </>
  );
}
